import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFInsuranceAnalysis = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('calculator');
    const [insuranceData, setInsuranceData] = useState({});
    const [recommendations, setRecommendations] = useState([]);
    const [riskAssessment, setRiskAssessment] = useState({});
    
    // Calculator Inputs (Tests 859-865)
    const [yourAge, setYourAge] = useState('');
    const [occupation, setOccupation] = useState('office');
    const [annualIncome, setAnnualIncome] = useState(0);
    const [debtMortgage, setDebtMortgage] = useState(0);
    const [dependents, setDependents] = useState(0);
    const [existingLife, setExistingLife] = useState(0);
    const [existingTPD, setExistingTPD] = useState(0);
    const [existingIP, setExistingIP] = useState(0);
    const [hasCalculated, setHasCalculated] = useState(false);

    // Insurance types and their analysis
    const insuranceTypes = {
        life: {
            name: 'Life Insurance',
            description: 'Protection for SMSF members and beneficiaries',
            icon: '🛡️',
            coverage: {
                recommended: 1500000,
                current: 0,
                adequacy: 'Inadequate'
            },
            factors: [
                'Member age and health status',
                'Dependent obligations',
                'SMSF balance and growth potential',
                'Debt obligations'
            ]
        },
        tpd: {
            name: 'Total & Permanent Disability',
            description: 'Coverage for permanent disability scenarios',
            icon: '♿',
            coverage: {
                recommended: 800000,
                current: 0,
                adequacy: 'Not Covered'
            },
            factors: [
                'Occupation risk level',
                'Income replacement needs',
                'Medical expenses',
                'Lifestyle maintenance costs'
            ]
        },
        income: {
            name: 'Income Protection',
            description: 'Temporary income replacement during illness/injury',
            icon: '💰',
            coverage: {
                recommended: 120000,
                current: 0,
                adequacy: 'Not Covered'
            },
            factors: [
                'Monthly living expenses',
                'SMSF contribution capacity',
                'Waiting period preferences',
                'Benefit period requirements'
            ]
        },
        trauma: {
            name: 'Trauma Insurance',
            description: 'Lump sum for critical illness events',
            icon: '🏥',
            coverage: {
                recommended: 400000,
                current: 0,
                adequacy: 'Not Covered'
            },
            factors: [
                'Family medical history',
                'Lifestyle risk factors',
                'Treatment cost estimates',
                'Recovery period needs'
            ]
        }
    };

    // Risk assessment matrix
    const performRiskAssessment = () => {
        const risks = {
            life: {
                level: 'High',
                impact: 'Critical',
                probability: 'Medium',
                factors: ['Dependent family members', 'High SMSF balance', 'Significant debt']
            },
            tpd: {
                level: 'Medium',
                impact: 'High',
                probability: 'Low',
                factors: ['Physical occupation', 'Age-related risks', 'Lifestyle factors']
            },
            income: {
                level: 'High',
                impact: 'Medium',
                probability: 'Medium',
                factors: ['Self-employed status', 'No sick leave', 'High monthly expenses']
            },
            trauma: {
                level: 'Medium',
                impact: 'High',
                probability: 'Low',
                factors: ['Family history', 'Stress levels', 'Preventive care gaps']
            }
        };
        return risks;
    };

    // Generate insurance recommendations
    const generateRecommendations = () => {
        return [
            {
                priority: 'High',
                type: 'life',
                title: 'Implement Life Insurance Coverage',
                description: 'Secure life insurance to protect SMSF members and beneficiaries',
                benefit: 'Ensures SMSF continuity and financial security for dependents',
                cost: 'Estimated $2,400-3,600 annually',
                action: 'Contact insurance broker for quotes'
            },
            {
                priority: 'High',
                type: 'income',
                title: 'Establish Income Protection',
                description: 'Protect against temporary income loss due to illness/injury',
                benefit: 'Maintains SMSF contributions during recovery periods',
                cost: 'Estimated $1,800-2,400 annually',
                action: 'Compare policies with 30-day waiting period'
            },
            {
                priority: 'Medium',
                type: 'tpd',
                title: 'Consider TPD Insurance',
                description: 'Coverage for permanent disability scenarios',
                benefit: 'Provides lump sum for permanent lifestyle changes',
                cost: 'Estimated $1,200-1,800 annually',
                action: 'Assess occupation-specific risks'
            },
            {
                priority: 'Medium',
                type: 'trauma',
                title: 'Evaluate Trauma Insurance',
                description: 'Protection against critical illness events',
                benefit: 'Immediate funds for medical treatment and recovery',
                cost: 'Estimated $800-1,200 annually',
                action: 'Review family medical history'
            }
        ];
    };

    // Insurance cost analysis
    const performCostAnalysis = () => {
        const totalRecommended = Object.values(insuranceTypes).reduce(
            (sum, insurance) => sum + insurance.coverage.recommended, 0
        );
        
        const monthlyPremium = totalRecommended * 0.002; // 2.4% annually
        const annualPremium = monthlyPremium * 12;
        
        return {
            totalCoverage: totalRecommended,
            monthlyPremium,
            annualPremium,
            costRatio: (annualPremium / 300000) * 100, // As % of annual income
            affordability: annualPremium < 15000 ? 'Affordable' : 'Review Required'
        };
    };

    useEffect(() => {
        setRiskAssessment(performRiskAssessment());
        setRecommendations(generateRecommendations());
        setInsuranceData(performCostAnalysis());
    }, []);

    const renderCalculator = () => {
        // Calculate recommendations
        const multiplier = occupation === 'manual' ? 12 : occupation === 'professional' ? 8 : 10;
        const recommendedLife = (annualIncome * multiplier) + debtMortgage + (dependents * 100000);
        const recommendedTPD = annualIncome * 8;
        const recommendedIP = annualIncome * 0.75 / 12; // Monthly
        
        const lifeGap = recommendedLife - existingLife;
        const tpdGap = recommendedTPD - existingTPD;
        const ipGap = recommendedIP - existingIP;
        
        const estimatedPremium = (yourAge ? (recommendedLife * 0.001 * (1 + (yourAge - 30) * 0.02)) + (recommendedTPD * 0.0015) + (recommendedIP * 12 * 0.03) : 0);
        
        return (
            <div className="space-y-6">
                {/* Input Section */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Insurance Needs Calculator</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Your Age</label>
                            <input
                                {...getNumericInputProps()}
                                value={yourAge}
                                onChange={(e) => setYourAge(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="45"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Occupation</label>
                            <select
                                value={occupation}
                                onChange={(e) => setOccupation(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="office">Office/Admin (Low Risk)</option>
                                <option value="professional">Professional (Medium Risk)</option>
                                <option value="manual">Manual/Trade (High Risk)</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Risk category determined</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Annual Income ($)</label>
                            <input
                                {...getNumericInputProps()}
                                value={annualIncome}
                                onChange={(e) => setAnnualIncome(Number(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="100000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Debt/Mortgage ($)</label>
                            <input
                                {...getNumericInputProps()}
                                value={debtMortgage}
                                onChange={(e) => setDebtMortgage(Number(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="500000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Number of Dependents</label>
                            <input
                                {...getNumericInputProps()}
                                value={dependents}
                                onChange={(e) => setDependents(Number(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="2"
                            />
                        </div>
                    </div>
                    
                    <div className="mt-6">
                        <h4 className="font-medium text-gray-700 mb-3">Existing Insurance Cover</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Life Insurance ($)</label>
                                <input
                                    {...getNumericInputProps()}
                                    value={existingLife}
                                    onChange={(e) => setExistingLife(Number(e.target.value) || 0)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">TPD Insurance ($)</label>
                                <input
                                    {...getNumericInputProps()}
                                    value={existingTPD}
                                    onChange={(e) => setExistingTPD(Number(e.target.value) || 0)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Income Protection ($/month)</label>
                                <input
                                    {...getNumericInputProps()}
                                    value={existingIP}
                                    onChange={(e) => setExistingIP(Number(e.target.value) || 0)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={() => setHasCalculated(true)}
                            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                        >
                            Calculate Insurance Needs
                        </button>
                    </div>
                </div>
                
                {/* Results */}
                {hasCalculated && (
                    <>
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Insurance Recommendations</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                {/* Test 866: Recommended Life Cover */}
                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="text-sm text-gray-600 mb-1">Recommended Life Cover</div>
                                    <div className="text-2xl font-bold text-blue-700">${recommendedLife.toLocaleString()}</div>
                                    <div className="text-xs text-gray-500 mt-1">Total protection needed</div>
                                </div>
                                
                                {/* Test 867: Recommended TPD */}
                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <div className="text-sm text-gray-600 mb-1">Recommended TPD</div>
                                    <div className="text-2xl font-bold text-purple-700">${recommendedTPD.toLocaleString()}</div>
                                    <div className="text-xs text-gray-500 mt-1">Disability cover</div>
                                </div>
                                
                                {/* Test 868: Recommended IP */}
                                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                    <div className="text-sm text-gray-600 mb-1">Recommended Income Protection</div>
                                    <div className="text-2xl font-bold text-green-700">${recommendedIP.toLocaleString()}/mo</div>
                                    <div className="text-xs text-gray-500 mt-1">Monthly income replacement</div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Test 869: Coverage Gap */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Coverage Gap Analysis</h3>
                            <div className="space-y-3">
                                <div className={`p-4 rounded-lg border ${lifeGap > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">Life Insurance Gap</span>
                                        <span className={`text-xl font-bold ${lifeGap > 0 ? 'text-red-700' : 'text-green-700'}`}>
                                            {lifeGap > 0 ? 'Under-insured' : 'Adequately covered'}
                                        </span>
                                    </div>
                                    {lifeGap > 0 && (
                                        <p className="text-sm text-red-600 mt-2">Shortfall: ${lifeGap.toLocaleString()}</p>
                                    )}
                                </div>
                                <div className={`p-4 rounded-lg border ${tpdGap > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">TPD Gap</span>
                                        <span className={`text-xl font-bold ${tpdGap > 0 ? 'text-red-700' : 'text-green-700'}`}>
                                            {tpdGap > 0 ? 'Under-insured' : 'Adequately covered'}
                                        </span>
                                    </div>
                                    {tpdGap > 0 && (
                                        <p className="text-sm text-red-600 mt-2">Shortfall: ${tpdGap.toLocaleString()}</p>
                                    )}
                                </div>
                                <div className={`p-4 rounded-lg border ${ipGap > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">Income Protection Gap</span>
                                        <span className={`text-xl font-bold ${ipGap > 0 ? 'text-red-700' : 'text-green-700'}`}>
                                            {ipGap > 0 ? 'Under-insured' : 'Adequately covered'}
                                        </span>
                                    </div>
                                    {ipGap > 0 && (
                                        <p className="text-sm text-red-600 mt-2">Shortfall: ${ipGap.toLocaleString()}/month</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Test 870: Estimated Premium */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Cost Estimate</h3>
                            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">Estimated Annual Premium</span>
                                    <span className="text-3xl font-bold text-orange-700">${estimatedPremium.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Based on age, occupation, and coverage amounts</p>
                            </div>
                        </div>
                        
                        {/* Test 871: Tax Deductibility */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Tax Deductibility</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <h4 className="font-semibold text-green-800 mb-2">Inside Super (SMSF)</h4>
                                    <ul className="text-sm text-green-700 space-y-1">
                                        <li>✅ Life & TPD premiums tax deductible</li>
                                        <li>✅ Reduces taxable income for fund</li>
                                        <li>✅ 15% tax on premiums vs your marginal rate</li>
                                    </ul>
                                </div>
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="font-semibold text-blue-800 mb-2">Outside Super (Personal)</h4>
                                    <ul className="text-sm text-blue-700 space-y-1">
                                        <li>• Income Protection fully deductible</li>
                                        <li>• Life & TPD not deductible personally</li>
                                        <li>• Pay at your marginal tax rate</li>
                                    </ul>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-4">
                                <strong>Recommendation:</strong> Hold Life & TPD inside SMSF for tax efficiency. 
                                Consider Income Protection outside super for full deductibility.
                            </p>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Insurance Status Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Insurance Coverage Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(insuranceTypes).map(([key, insurance]) => (
                        <div key={key} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-2">{insurance.icon}</span>
                                <div>
                                    <h4 className="font-semibold text-gray-800">{insurance.name}</h4>
                                    <p className="text-xs text-gray-600">{insurance.description}</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Recommended:</span>
                                    <span className="font-medium">${insurance.coverage.recommended.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Current:</span>
                                    <span className="font-medium text-red-600">${insurance.coverage.current.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Status:</span>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                        insurance.coverage.adequacy === 'Adequate' ? 'bg-green-100 text-green-800' :
                                        insurance.coverage.adequacy === 'Inadequate' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        {insurance.coverage.adequacy}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Risk Assessment Matrix */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Risk Assessment Matrix</h3>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4">Insurance Type</th>
                                <th className="text-center py-3 px-4">Risk Level</th>
                                <th className="text-center py-3 px-4">Impact</th>
                                <th className="text-center py-3 px-4">Probability</th>
                                <th className="text-left py-3 px-4">Key Risk Factors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(riskAssessment).map(([key, assessment]) => (
                                <tr key={key} className="border-b border-gray-100">
                                    <td className="py-3 px-4 font-medium">{insuranceTypes[key]?.name}</td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            assessment.level === 'High' ? 'bg-red-100 text-red-800' :
                                            assessment.level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'
                                        }`}>
                                            {assessment.level}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            assessment.impact === 'Critical' ? 'bg-red-100 text-red-800' :
                                            assessment.impact === 'High' ? 'bg-orange-100 text-orange-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {assessment.impact}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            assessment.probability === 'High' ? 'bg-red-100 text-red-800' :
                                            assessment.probability === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'
                                        }`}>
                                            {assessment.probability}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-600">
                                        {assessment.factors.join(', ')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Cost Analysis */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Insurance Cost Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">
                            ${insuranceData.totalCoverage?.toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-700">Total Recommended Coverage</div>
                    </div>
                    <div className="text-center p-6 bg-green-50 rounded-lg">
                        <div className="text-3xl font-bold text-green-600">
                            ${insuranceData.annualPremium?.toLocaleString()}
                        </div>
                        <div className="text-sm text-green-700">Annual Premium Cost</div>
                    </div>
                    <div className="text-center p-6 bg-purple-50 rounded-lg">
                        <div className="text-3xl font-bold text-purple-600">
                            {insuranceData.costRatio?.toFixed(1)}%
                        </div>
                        <div className="text-sm text-purple-700">Cost as % of Income</div>
                    </div>
                </div>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700">Affordability Assessment:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            insuranceData.affordability === 'Affordable' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                            {insuranceData.affordability}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderRecommendations = () => (
        <div className="space-y-6">
            {/* Priority Recommendations */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Insurance Recommendations</h3>
                <div className="space-y-4">
                    {recommendations.map((rec, index) => (
                        <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                            rec.priority === 'High' ? 'border-red-500 bg-red-50' :
                            rec.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
                            'border-blue-500 bg-blue-50'
                        }`}>
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-800">{rec.title}</h4>
                                    <p className="text-sm text-gray-600 mb-2">{rec.description}</p>
                                    <p className="text-sm text-green-700 font-medium">{rec.benefit}</p>
                                </div>
                                <span className={`px-2 py-1 text-xs rounded ml-4 ${
                                    rec.priority === 'High' ? 'bg-red-100 text-red-800' :
                                    rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-blue-100 text-blue-800'
                                }`}>
                                    {rec.priority} Priority
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-600">
                                    <span className="font-medium">Estimated Cost:</span> {rec.cost}
                                </div>
                                <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
                                    {rec.action}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Implementation Timeline */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Recommended Implementation Timeline</h3>
                <div className="space-y-4">
                    <div className="flex items-center p-4 bg-red-50 rounded-lg">
                        <div className="w-3 h-3 bg-red-500 rounded-full mr-4"></div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">Immediate (0-30 days)</h4>
                            <p className="text-sm text-gray-600">Life Insurance and Income Protection</p>
                        </div>
                    </div>
                    <div className="flex items-center p-4 bg-yellow-50 rounded-lg">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full mr-4"></div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">Short-term (1-3 months)</h4>
                            <p className="text-sm text-gray-600">TPD Insurance evaluation and implementation</p>
                        </div>
                    </div>
                    <div className="flex items-center p-4 bg-blue-50 rounded-lg">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-4"></div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-800">Medium-term (3-6 months)</h4>
                            <p className="text-sm text-gray-600">Trauma Insurance assessment and coverage</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Provider Comparison */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Insurance Provider Comparison</h3>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4">Provider</th>
                                <th className="text-center py-3 px-4">Life Insurance</th>
                                <th className="text-center py-3 px-4">Income Protection</th>
                                <th className="text-center py-3 px-4">TPD Insurance</th>
                                <th className="text-center py-3 px-4">Trauma Insurance</th>
                                <th className="text-center py-3 px-4">Overall Rating</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { name: 'TAL', life: 'A+', income: 'A+', tpd: 'A', trauma: 'A+', rating: 'A+' },
                                { name: 'AIA', life: 'A+', income: 'A', tpd: 'A+', trauma: 'A', rating: 'A' },
                                { name: 'Zurich', life: 'A', income: 'A+', tpd: 'A', trauma: 'A', rating: 'A' },
                                { name: 'OnePath', life: 'A', income: 'A', tpd: 'A', trauma: 'A', rating: 'A' },
                                { name: 'MLC', life: 'A', income: 'A-', tpd: 'A', trauma: 'A-', rating: 'A-' }
                            ].map((provider, index) => (
                                <tr key={index} className="border-b border-gray-100">
                                    <td className="py-3 px-4 font-medium">{provider.name}</td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            provider.life.includes('A+') ? 'bg-green-100 text-green-800' :
                                            provider.life.includes('A') ? 'bg-blue-100 text-blue-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {provider.life}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            provider.income.includes('A+') ? 'bg-green-100 text-green-800' :
                                            provider.income.includes('A') ? 'bg-blue-100 text-blue-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {provider.income}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            provider.tpd.includes('A+') ? 'bg-green-100 text-green-800' :
                                            provider.tpd.includes('A') ? 'bg-blue-100 text-blue-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {provider.tpd}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            provider.trauma.includes('A+') ? 'bg-green-100 text-green-800' :
                                            provider.trauma.includes('A') ? 'bg-blue-100 text-blue-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {provider.trauma}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            provider.rating.includes('A+') ? 'bg-green-100 text-green-800' :
                                            provider.rating.includes('A') ? 'bg-blue-100 text-blue-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {provider.rating}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderClaimsProcess = () => (
        <div className="space-y-6">
            {/* Claims Process Overview */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Insurance Claims Process</h3>
                <div className="space-y-4">
                    {[
                        { step: 1, title: 'Notification', description: 'Notify insurer within 30 days of event', duration: 'Immediate', documentation: 'Initial claim form, medical certificates' },
                        { step: 2, title: 'Assessment', description: 'Insurer assesses claim and requests additional information', duration: '2-4 weeks', documentation: 'Medical reports, financial statements' },
                        { step: 3, title: 'Investigation', description: 'Independent medical and financial investigation', duration: '4-8 weeks', documentation: 'Specialist reports, employment records' },
                        { step: 4, title: 'Decision', description: 'Insurer makes decision and communicates outcome', duration: '1-2 weeks', documentation: 'Decision letter with reasoning' },
                        { step: 5, title: 'Payment', description: 'Payment processed if claim approved', duration: '1 week', documentation: 'Banking details, final paperwork' }
                    ].map((step, index) => (
                        <div key={index} className="flex items-start p-4 border border-gray-200 rounded-lg">
                            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-4">
                                {step.step}
                            </div>
                            <div className="flex-1">
                                <h4 className="font-semibold text-gray-800">{step.title}</h4>
                                <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                                <div className="flex justify-between items-center text-xs text-gray-500">
                                    <span><strong>Duration:</strong> {step.duration}</span>
                                    <span><strong>Documents:</strong> {step.documentation}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Claims Statistics */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Industry Claims Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { type: 'Life Insurance', approval: 94, avgTime: '21 days', avgAmount: '$850K' },
                        { type: 'Income Protection', approval: 87, avgTime: '35 days', avgAmount: '$85K' },
                        { type: 'TPD Insurance', approval: 76, avgTime: '62 days', avgAmount: '$420K' },
                        { type: 'Trauma Insurance', approval: 89, avgTime: '28 days', avgAmount: '$180K' }
                    ].map((stat, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg text-center">
                            <h4 className="font-semibold text-gray-800 mb-2">{stat.type}</h4>
                            <div className="space-y-2">
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{stat.approval}%</div>
                                    <div className="text-xs text-gray-600">Approval Rate</div>
                                </div>
                                <div>
                                    <div className="text-lg font-bold text-blue-600">{stat.avgTime}</div>
                                    <div className="text-xs text-gray-600">Avg Processing Time</div>
                                </div>
                                <div>
                                    <div className="text-lg font-bold text-purple-600">{stat.avgAmount}</div>
                                    <div className="text-xs text-gray-600">Average Payout</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Insurance Analysis</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('calculator')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'calculator' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Calculator
                    </button>
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('recommendations')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'recommendations' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Recommendations
                    </button>
                    <button
                        onClick={() => setActiveTab('claims')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'claims' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Claims Process
                    </button>
                </div>
            </div>

            {/* Content based on active tab */}
            {activeTab === 'calculator' && renderCalculator()}
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'recommendations' && renderRecommendations()}
            {activeTab === 'claims' && renderClaimsProcess()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Analysis as of: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                            Export Insurance Report
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Contact Insurance Broker
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFInsuranceAnalysis.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFInsuranceAnalysis;
